import { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Flex,
  Spinner,
  Image,
  useColorModeValue,
} from '@chakra-ui/react'
import { ChatTeardropDots, Calendar, User, Download, EnvelopeSimple } from '@phosphor-icons/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import api from '../../services/api'

interface ChatData {
  id: number
  status: string
  protocol: string | null
  createdAt: string
  resolvedAt: string | null
  ownerName: string
  ownerEmail: string
  agentName: string | null
  categoryName: string | null
  categoryColor: string | null
}

interface ChatMessage {
  id: number
  message: string | null
  senderType: string
  senderId: number | null
  messageType: string
  fileUrl: string | null
  fileType: string | null
  fileName: string | null
  fileSize: number | null
  caption: string | null
  createdAt: string
  senderName: string | null
  senderProfilePic: string | null
}

const chatStatusLabels: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em Andamento',
  completed: 'Concluído',
}

const chatStatusColors: Record<string, string> = {
  pending: 'yellow',
  in_progress: 'blue',
  completed: 'green',
}

export default function ChatDetailModal({
  isOpen,
  onClose,
  chat,
  chatId,
}: {
  isOpen: boolean
  onClose: () => void
  chat: ChatData
  chatId: number
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    if (isOpen && chatId) {
      fetchMessages()
    }
  }, [isOpen, chatId])

  const fetchMessages = async () => {
    setIsLoading(true)
    try {
      const response = await api.get(`/messages/${chatId}`, {
        params: { limit: 100, page: 1 },
      })
      const raw = response.data.data || response.data || []
      raw.sort((a: ChatMessage, b: ChatMessage) => Number(a.id) - Number(b.id))
      setMessages(raw)
    } catch (error) {
      console.error('Error fetching chat messages:', error)
      setMessages([])
    } finally {
      setIsLoading(false)
    }
  }

  const renderMessageContent = (msg: ChatMessage, isAgent: boolean) => {
    const msgType = msg.messageType || 'text'

    return (
      <>
        {msgType === 'image' && msg.fileUrl && (
          <Image
            src={msg.fileUrl}
            alt={msg.fileName || 'Imagem'}
            maxH="200px"
            borderRadius="md"
            mb={1}
            cursor="pointer"
            onClick={() => window.open(msg.fileUrl!, '_blank')}
          />
        )}
        {msgType === 'video' && msg.fileUrl && (
          <Box as="video" src={msg.fileUrl} controls maxH="200px" borderRadius="md" mb={1} w="100%" />
        )}
        {msgType === 'audio' && msg.fileUrl && (
          <Box as="audio" src={msg.fileUrl} controls w="100%" mb={1} />
        )}
        {msgType === 'file' && msg.fileUrl && (
          <HStack
            bg={isAgent ? 'yellow.700' : 'gray.600'}
            p={2}
            borderRadius="md"
            mb={1}
            as="a"
            href={msg.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Download size={16} />
            <Text fontSize="xs" noOfLines={1}>{msg.fileName || 'Arquivo'}</Text>
          </HStack>
        )}
        {msg.caption && (
          <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word" mb={msg.message ? 0 : undefined}>
            {msg.caption}
          </Text>
        )}
        {msg.message && msgType === 'text' && (
          <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
            {msg.message}
          </Text>
        )}
        {msg.message && msgType !== 'text' && !msg.caption && (
          <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
            {msg.message}
          </Text>
        )}
      </>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg={surfaceBg} maxH="85vh">
        <ModalHeader borderBottom="1px" borderColor={borderColor}>
          <HStack>
            <ChatTeardropDots size={20} color="#ECC94B" />
            <Text color={textPrimary} fontSize="md">
              Atendimento {chat.protocol || `#${chat.id}`}
            </Text>
            <Badge colorScheme={chatStatusColors[chat.status] || 'gray'}>
              {chatStatusLabels[chat.status] || chat.status}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color={textMuted} />
        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            {/* Info do chat */}
            <Box bg={subtleBg} p={3} borderRadius="md">
              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <HStack spacing={1}>
                    <User size={12} color="#A0AEC0" />
                    <Text fontSize="xs" color={textMuted}>Usuário:</Text>
                  </HStack>
                  <Text fontSize="xs" color={textPrimary}>{chat.ownerName}</Text>
                </HStack>
                {chat.ownerEmail && (
                  <HStack justify="space-between">
                    <HStack spacing={1}>
                      <EnvelopeSimple size={12} color="#A0AEC0" />
                      <Text fontSize="xs" color={textMuted}>Email:</Text>
                    </HStack>
                    <Text fontSize="xs" color={textPrimary}>{chat.ownerEmail}</Text>
                  </HStack>
                )}
                {chat.agentName && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Atendente:</Text>
                    <Text fontSize="xs" color={textPrimary}>{chat.agentName}</Text>
                  </HStack>
                )}
                {chat.categoryName && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Categoria:</Text>
                    <Badge fontSize="2xs" bg={chat.categoryColor || 'blue.500'} color="white">
                      {chat.categoryName}
                    </Badge>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <HStack spacing={1}>
                    <Calendar size={12} color="#A0AEC0" />
                    <Text fontSize="xs" color={textMuted}>Criado em:</Text>
                  </HStack>
                  <Text fontSize="xs" color={textPrimary}>
                    {format(new Date(chat.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </Text>
                </HStack>
                {chat.resolvedAt && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color={textMuted}>Resolvido em:</Text>
                    <Text fontSize="xs" color="green.400">
                      {format(new Date(chat.resolvedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Mensagens */}
            <Text fontSize="sm" fontWeight="semibold" color={textSecondary}>
              Mensagens ({messages.length})
            </Text>

            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner size="lg" color="yellow.500" />
              </Flex>
            ) : messages.length === 0 ? (
              <Text fontSize="sm" color={textMuted} textAlign="center" py={4}>
                Nenhuma mensagem encontrada
              </Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {messages.map((msg) => {
                  const isAgent = msg.senderType === 'agent'
                  const isSystem = msg.senderType === 'system'

                  if (isSystem) {
                    return (
                      <Flex key={msg.id} justify="center">
                        <Text
                          fontSize="xs"
                          color={textMuted}
                          bg={subtleBg}
                          px={3}
                          py={1}
                          borderRadius="full"
                          textAlign="center"
                          textTransform="uppercase"
                          fontWeight="semibold"
                        >
                          {msg.message}
                        </Text>
                      </Flex>
                    )
                  }

                  return (
                    <Flex key={msg.id} justify={isAgent ? 'flex-end' : 'flex-start'}>
                      <Box
                        maxW="80%"
                        bg={isAgent ? 'yellow.600' : subtleBg}
                        color="white"
                        px={3}
                        py={2}
                        borderRadius="lg"
                        borderTopRightRadius={isAgent ? '2px' : 'lg'}
                        borderTopLeftRadius={isAgent ? 'lg' : '2px'}
                      >
                        <Text fontSize="xs" fontWeight="bold" mb={1} color={isAgent ? 'yellow.100' : 'blue.300'}>
                          {msg.senderName || (isAgent ? 'Atendente' : 'Usuário')}
                        </Text>
                        {renderMessageContent(msg, isAgent)}
                        <Text fontSize="2xs" color={isAgent ? 'yellow.200' : 'gray.500'} textAlign="right" mt={1}>
                          {format(new Date(msg.createdAt), 'dd/MM/yy HH:mm', { locale: ptBR })}
                        </Text>
                      </Box>
                    </Flex>
                  )
                })}
              </VStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
